import { forwardRef, PropsWithChildren } from 'react'
import { ButtonProps } from 'blaze-components/WriterTopBar/Types'
import { useObjectRef } from 'utils/useObjectRef'
import { useButton } from '@react-aria/button'
import { useHover } from '@react-aria/interactions'
import { useFocusRing } from '@react-aria/focus'
import { filterDOMProps, mergeProps } from '@react-aria/utils'
import cx from 'classnames'
import Styles from './Button.module.scss'
import { DOMProps } from '@react-types/shared'

export const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>((props, fRef) => {
  const ref = useObjectRef<HTMLButtonElement>(fRef)
  const { children, icon: Icon, isDisabled, variant = 'toggleOff', ...finalProps } = props
  const { buttonProps, isPressed } = useButton(props, ref)
  const { hoverProps, isHovered } = useHover(props)
  const { isFocusVisible, focusProps } = useFocusRing(props)

  return (
    <button
      {...mergeProps(
        filterDOMProps(finalProps),
        buttonProps,
        hoverProps,
        focusProps,
        (props.onPointerEnter
          ? { onPointerEnter: props.onPointerEnter, onPointerLeave: props.onPointerLeave }
          : {}) as DOMProps,
      )}
      className={cx(Styles.root, Styles[variant], {
        [Styles.hovered]: isHovered,
        [Styles.pressed]: isPressed,
        [Styles.focused]: isFocusVisible,
        [Styles.disabled]: isDisabled,
      })}
      ref={ref}
    >
      {Icon && (
        <span className={Styles.icon}>
          <Icon />
        </span>
      )}
      {children && <span className={Styles.children}>{children}</span>}
    </button>
  )
})

export const ToggleButton = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<Omit<ButtonProps, 'variant'> & { on?: boolean }>
>(({ children, on = false, ...props }, fRef) => (
  <Button {...props} variant={on ? 'toggleOn' : 'toggleOff'} aria-pressed={on}>
    {children}
  </Button>
))
